import { SRGBColorSpace, type Texture, TextureLoader } from "three";

const urls = import.meta.glob("../assets/textures/*.{jpg,png}", {
  eager: true,
  query: "?url",
  import: "default",
}) as Record<string, string>;
const byName = new Map(
  Object.entries(urls).map(([path, url]) => [path.slice(path.lastIndexOf("/") + 1), url]),
);

export function textureUrl(name: string): string | undefined {
  return byName.get(name);
}

/** The part of TextureLoader the manager uses; tests pass a stub. */
export interface ImageLoader {
  load(
    url: string,
    onLoad: (t: Texture) => void,
    onProgress: undefined,
    onError: (err: unknown) => void,
  ): unknown;
}

export class TextureManager {
  /** Network loads started and finished (loaded or failed); cache hits are not counted. */
  requested = 0;
  completed = 0;
  private readonly cache = new Map<string, Texture>();
  private readonly pending = new Map<string, ((t: Texture) => void)[]>();
  maxAnisotropy = 1;

  constructor(private readonly loader: ImageLoader = new TextureLoader()) {}

  load(name: string, onLoad: (t: Texture) => void): void {
    const cached = this.cache.get(name);
    if (cached) {
      onLoad(cached);
      return;
    }
    const waiting = this.pending.get(name);
    if (waiting) {
      waiting.push(onLoad);
      return;
    }
    const url = textureUrl(name);
    if (!url) {
      console.warn(`texture not shipped: ${name}`);
      return;
    }
    this.pending.set(name, [onLoad]);
    this.requested++;
    this.loader.load(
      url,
      (t) => {
        this.completed++;
        t.colorSpace = SRGBColorSpace;
        t.anisotropy = this.maxAnisotropy;
        this.cache.set(name, t);
        for (const cb of this.pending.get(name) ?? []) cb(t);
        this.pending.delete(name);
      },
      undefined,
      () => {
        this.completed++;
        console.warn(`texture failed to load: ${name}`);
        this.pending.delete(name);
      },
    );
  }
}
